// InlinePaymentSelector: the card-less + wallet-available payment picker for booking
// Step Five. Replaces the payment dropdown for brand-new users when a device wallet is
// present: a radio toggle with Apple/Google Pay on top (default selected) and "Credit
// card" below, which expands the inline NewPaymentMethod form. Users WITH saved cards
// keep the dropdown; card-less users with NO wallet keep the plain inline form.
import React, { useEffect, useState, useRef } from 'react'
import styled from '@emotion/styled'
import PlatformPayDetails, { getWalletDisplay } from './PlatformPayDetails'
import NewPaymentMethod from './NewPaymentMethod'
import { Text } from '../../styles/Components'
import { PLATFORM_PAY } from '../../helpers'

const SelectorList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 24px 0 0;

  li {
    width: 100%;
  }

  .payment-option {
    align-items: center;
    background-color: transparent;
    border: none;
    cursor: pointer;
    display: flex;
    font-family: Euclid;
    gap: 12px;
    padding: 12px 0;
    text-align: left;
    width: 100%;

    p {
      font-size: 16px !important;
    }
  }
`

const CardFormWrap = styled.div`
  margin-top: 22px;
`

// Radio control: unselected is a light-grey filled circle; selected is a black circle
// with a white check (Cro Sprint Figma). Inlined as SVG so it's fully theme-independent
// and needs no asset. Decorative — selection state is exposed via aria-pressed on the row.
const RadioCircle = ({ selected }) =>
  selected ? (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" style={{ flex: '0 0 auto' }}>
      <circle cx="12" cy="12" r="12" fill="#000000" />
      <path
        d="M6.5 12.5l3.2 3.2 8-8"
        fill="none"
        stroke="#FFFFFF"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ) : (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" style={{ flex: '0 0 auto' }}>
      <circle cx="12" cy="12" r="12" fill="#F3F0F0" />
    </svg>
  )

/**
 * @param {object} props
 * @param {{applePay?: boolean, googlePay?: boolean}} props.platformPayAvailability - reported wallets
 * @param {string|null} props.selectedPaymentMethodId - current selection (PLATFORM_PAY, a card id, or null)
 * @param {(id: string|null) => void} props.selectPaymentMethod - set the selection (PLATFORM_PAY for wallet, null for card)
 * @param {(method: object) => void} props.createdPaymentMethodFn - called after a card is saved
 * @param {Array} props.paymentMethods - existing payment methods (empty in this branch)
 * @param {object} props.me - current user
 * @param {string|null} props.error - booking error to surface above the rows
 */
const InlinePaymentSelector = ({
  platformPayAvailability,
  selectedPaymentMethodId,
  selectPaymentMethod,
  createdPaymentMethodFn,
  paymentMethods,
  me,
  error,
}) => {
  const wallet = getWalletDisplay(platformPayAvailability)
  // Local toggle state drives what's shown so the card form never flashes before the
  // default-select effect has synced global state. Defaults to the wallet, which is why
  // a brand-new user lands on platform pay.
  const [option, setOption] = useState('wallet')
  const defaultApplied = useRef(false)

  // Default a brand-new user to platform pay once. This selector only mounts when a
  // wallet is available AND there's a chargeable total (PaymentCard/Five gate on
  // platformPayEligible), so mounting is the same signal the old Five default-select
  // effect keyed off. Owning it here means choosing Credit card (dispatch null) can't be
  // snapped back to PLATFORM_PAY by that effect.
  useEffect(() => {
    if (!selectedPaymentMethodId && !defaultApplied.current) {
      defaultApplied.current = true
      selectPaymentMethod(PLATFORM_PAY, 'default')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const chooseWallet = () => {
    setOption('wallet')
    selectPaymentMethod(PLATFORM_PAY)
  }

  const chooseCard = () => {
    setOption('card')
    // null (not the PLATFORM_PAY sentinel, never a real payment-method id) so the footer
    // shows a disabled Book Now until the card is saved via the form below.
    selectPaymentMethod(null)
  }

  const walletSelected = option === 'wallet'
  const cardSelected = option === 'card'

  return (
    <div className="inline-payment-selector">
      {error && (
        <p className="payment-error" role="alert">
          {error}
        </p>
      )}
      <SelectorList>
        {wallet && (
          <li>
            <button
              type="button"
              className="payment-option"
              data-testid="platform-pay-select"
              aria-pressed={walletSelected}
              onClick={chooseWallet}
            >
              <RadioCircle selected={walletSelected} />
              <PlatformPayDetails label={wallet.label} mark={wallet.mark} />
            </button>
          </li>
        )}
        <li>
          <button
            type="button"
            className="payment-option"
            data-testid="credit-card-select"
            aria-pressed={cardSelected}
            onClick={chooseCard}
          >
            <RadioCircle selected={cardSelected} />
            <Text fontSize={18}>Credit card</Text>
          </button>
        </li>
      </SelectorList>
      {cardSelected && (
        <CardFormWrap>
          <NewPaymentMethod
            data-testid="new-payment-method"
            id="new-payment-method"
            showHeading={false}
            createdPaymentMethodFn={createdPaymentMethodFn}
            paymentMethods={paymentMethods}
            me={me}
          />
        </CardFormWrap>
      )}
    </div>
  )
}

export default InlinePaymentSelector
